package com.arenaproplay.admin.data.admin

import com.arenaproplay.admin.core.config.AppConfig
import com.arenaproplay.admin.core.logger.getLogger
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.domain.me.ChatListPage
import com.arenaproplay.admin.domain.me.ChatMessagesPage
import io.ktor.client.*
import io.ktor.client.request.*
import io.ktor.client.request.forms.*
import io.ktor.client.statement.*
import io.ktor.http.*
import io.ktor.http.content.TextContent
import kotlinx.serialization.DeserializationStrategy
import kotlinx.serialization.SerializationStrategy
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.serializer

/**
 * Talks to the backend /api/v1/admin/... endpoints. Responses come wrapped
 * as `{ "data": <payload> }` from AdminAnalyticsRoute; this class unwraps
 * that envelope before deserializing.
 */
class HttpAdminRepository(
    private val client: HttpClient,
) : AdminRepository {

    private val logger = getLogger<HttpAdminRepository>()
    private val json = Json { ignoreUnknownKeys = true; isLenient = true }

    private suspend fun <T> get(
        path: String,
        params: Map<String, String> = emptyMap(),
        deserializer: DeserializationStrategy<T>,
    ): T = getPath("/v1/admin$path", params, deserializer)

    /** Same as [get] but without the /v1/admin prefix — for shared (non-admin) endpoints. */
    private suspend fun <T> getPath(
        path: String,
        params: Map<String, String> = emptyMap(),
        deserializer: DeserializationStrategy<T>,
    ): T {
        val response =
            client.get(AppConfig.getApiUrl(path)) {
                params.forEach { (k, v) -> parameter(k, v) }
            }
        val text = response.bodyAsText()
        if (!response.status.isSuccess()) {
            logger.warn("GET $path failed ${response.status}: ${text.take(200)}")
            throw IllegalStateException("GET $path -> ${response.status}")
        }
        val envelope = json.parseToJsonElement(text).jsonObject
        val data: JsonElement = envelope["data"] ?: error("no `data` in: ${text.take(200)}")
        return json.decodeFromJsonElement(deserializer, data)
    }

    private suspend fun <B, T> postPath(
        path: String,
        body: B,
        bodySerializer: SerializationStrategy<B>,
        deserializer: DeserializationStrategy<T>,
    ): T {
        val response = client.post(AppConfig.getApiUrl(path)) {
            setBody(TextContent(json.encodeToString(bodySerializer, body), ContentType.Application.Json))
        }
        return unwrapData("POST", path, response, deserializer)
    }

    private suspend fun <B, T> putPath(
        path: String,
        body: B,
        bodySerializer: SerializationStrategy<B>,
        deserializer: DeserializationStrategy<T>,
    ): T {
        val response = client.put(AppConfig.getApiUrl(path)) {
            setBody(TextContent(json.encodeToString(bodySerializer, body), ContentType.Application.Json))
        }
        return unwrapData("PUT", path, response, deserializer)
    }

    private suspend fun deletePath(path: String) {
        val response = client.delete(AppConfig.getApiUrl(path))
        if (!response.status.isSuccess()) {
            val text = response.bodyAsText()
            logger.warn("DELETE $path failed ${response.status}: ${text.take(200)}")
            throw IllegalStateException(backendErrors(text) ?: "DELETE $path -> ${response.status}")
        }
    }

    /** Unwraps the `{ "data": ... }` envelope, surfacing backend `errors` on failure. */
    private suspend fun <T> unwrapData(
        method: String,
        path: String,
        response: HttpResponse,
        deserializer: DeserializationStrategy<T>,
    ): T {
        val text = response.bodyAsText()
        if (!response.status.isSuccess()) {
            logger.warn("$method $path failed ${response.status}: ${text.take(200)}")
            throw IllegalStateException(backendErrors(text) ?: "$method $path -> ${response.status}")
        }
        val envelope = json.parseToJsonElement(text).jsonObject
        val data: JsonElement = envelope["data"] ?: error("no `data` in: ${text.take(200)}")
        return json.decodeFromJsonElement(deserializer, data)
    }

    private fun backendErrors(text: String): String? = runCatching {
        json.parseToJsonElement(text).jsonObject["errors"]
            ?.jsonArray?.joinToString { it.jsonPrimitive.content }
    }.getOrNull()?.takeIf { it.isNotBlank() }

    private suspend fun delete(path: String) {
        val response = client.delete(AppConfig.getApiUrl("/v1/admin$path"))
        if (!response.status.isSuccess()) {
            val text = response.bodyAsText()
            logger.warn("Admin DELETE $path failed ${response.status}: ${text.take(200)}")
            throw IllegalStateException("admin DELETE $path -> ${response.status}")
        }
    }

    /** Bodyless admin POST for action endpoints that reply 204. */
    private suspend fun postNoContent(path: String) {
        val response = client.post(AppConfig.getApiUrl("/v1/admin$path"))
        if (!response.status.isSuccess()) {
            val text = response.bodyAsText()
            logger.warn("Admin POST $path failed ${response.status}: ${text.take(200)}")
            throw IllegalStateException(backendErrors(text) ?: "admin POST $path -> ${response.status}")
        }
    }

    /** Bodied admin PUT for action endpoints that reply 204. */
    private suspend fun <B> putNoContent(
        path: String,
        body: B,
        bodySerializer: SerializationStrategy<B>,
    ) {
        val response = client.put(AppConfig.getApiUrl("/v1/admin$path")) {
            setBody(TextContent(json.encodeToString(bodySerializer, body), ContentType.Application.Json))
        }
        if (!response.status.isSuccess()) {
            val text = response.bodyAsText()
            logger.warn("Admin PUT $path failed ${response.status}: ${text.take(200)}")
            throw IllegalStateException(backendErrors(text) ?: "admin PUT $path -> ${response.status}")
        }
    }

    override suspend fun deleteUser(userId: String) =
        delete("/users/$userId")

    override suspend fun grantPro(userId: String) =
        postNoContent("/users/$userId/subscription")

    override suspend fun revokePro(userId: String) =
        delete("/users/$userId/subscription")

    override suspend fun getUserPhotos(userId: String): UserPhotos =
        get("/moderation/users/$userId/photos", deserializer = UserPhotos.serializer())

    override suspend fun removeUserAvatar(userId: String, reason: String, target: String, cardId: String?): Boolean {
        val path = "/v1/admin/moderation/users/$userId/avatar/remove"
        val cardIdField = cardId?.let { ""","cardId":"$it"""" } ?: ""
        val response = client.post(AppConfig.getApiUrl(path)) {
            setBody(
                TextContent(
                    """{"reason":"$reason","target":"$target"$cardIdField}""",
                    ContentType.Application.Json,
                ),
            )
        }
        if (response.status.value == 409) {
            throw IllegalStateException("У юзера уже нет этого фото")
        }
        return unwrapData("POST", path, response, AvatarRemoveResult.serializer()).removed
    }

    override suspend fun getOverviewKpis(period: Period): OverviewKpis =
        get("/overview", mapOf("period" to period.code), serializer())

    override suspend fun getSubscriptionKpis(period: Period): SubscriptionKpis =
        get("/subscriptions/kpis", mapOf("period" to period.code), serializer())

    override suspend fun getAnalyticsBaseline(): String? =
        get("/analytics/baseline", deserializer = AnalyticsBaseline.serializer()).baseline

    override suspend fun resetAnalyticsBaseline(): String {
        val response = client.put(AppConfig.getApiUrl("/v1/admin/analytics/baseline"))
        val dto = unwrapData("PUT", "/analytics/baseline", response, AnalyticsBaseline.serializer())
        return dto.baseline ?: error("backend returned no baseline")
    }

    override suspend fun clearAnalyticsBaseline() =
        delete("/analytics/baseline")

    override suspend fun getUserGrowth(period: Period): CategorizedTimeSeries =
        get("/users/growth", mapOf("period" to period.code), serializer())

    override suspend fun getUsersByPlatform(period: Period): List<CategoryShare> =
        get(
            "/users/by-platform",
            mapOf("period" to period.code),
            ListSerializer(serializer<CategoryShare>()),
        )

    override suspend fun getSportPopularity(period: Period): List<CategoryShare> =
        get(
            "/sports/popularity",
            mapOf("period" to period.code),
            ListSerializer(serializer<CategoryShare>()),
        )

    override suspend fun getRecentActivity(limit: Int): List<ActivityItem> =
        get(
            "/activity/recent",
            mapOf("limit" to limit.toString()),
            ListSerializer(serializer<ActivityItem>()),
        )

    override suspend fun getPeople(page: Int, pageSize: Int): PageResult<AdminPerson> =
        get(
            "/people",
            mapOf("page" to page.toString(), "pageSize" to pageSize.toString()),
            serializer(),
        )

    override suspend fun getUsers(page: Int, pageSize: Int, query: String): PageResult<UserRow> =
        get(
            "/users",
            mapOf("page" to page.toString(), "pageSize" to pageSize.toString(), "query" to query),
            serializer(),
        )

    override suspend fun getEvents(page: Int, pageSize: Int, query: String): PageResult<EventRow> =
        get(
            "/events",
            mapOf("page" to page.toString(), "pageSize" to pageSize.toString(), "query" to query),
            serializer(),
        )

    override suspend fun getEventDetail(eventId: String): AdminEventDetail =
        get("/events/$eventId", emptyMap(), serializer())

    override suspend fun getEventStats(eventId: String): AdminEventStats =
        getPath("/v1/events/$eventId/stats", emptyMap(), serializer())

    override suspend fun confirmEventParticipant(eventId: String, userId: String) =
        postNoContent("/events/$eventId/participants/$userId/confirm")

    override suspend fun rejectEventParticipant(eventId: String, userId: String) =
        postNoContent("/events/$eventId/participants/$userId/reject")

    override suspend fun setEventStatus(eventId: String, status: String) =
        putNoContent("/events/$eventId/status", UpdateEventStatusPayload(status), serializer())

    override suspend fun setEventTeamScore(eventId: String, teamId: String, score: Int) =
        putNoContent("/events/$eventId/teams/$teamId/score", SetTeamScorePayload(score), serializer())

    override suspend fun setEventPlayerStats(eventId: String, userId: String, stats: List<EventStatEntryPayload>) =
        putNoContent("/events/$eventId/players/$userId/stats", SetPlayerStatsPayload(stats), serializer())

    override suspend fun saveEventTemplate(eventId: String, name: String): AdminEventTemplate =
        postPath("/v1/admin/events/$eventId/save-template", SaveEventTemplatePayload(name), serializer(), serializer())

    override suspend fun getEventTemplates(): List<AdminEventTemplate> =
        get("/event-templates", emptyMap(), ListSerializer(serializer<AdminEventTemplate>()))

    override suspend fun deleteEventTemplate(templateId: String) =
        deletePath("/v1/admin/event-templates/$templateId")

    override suspend fun createEventFromTemplate(templateId: String, dateTime: String, endDateTime: String?): CreateEventFromTemplateResult =
        postPath(
            "/v1/admin/event-templates/$templateId/create-event",
            CreateEventFromTemplatePayload(dateTime, endDateTime),
            serializer(),
            serializer(),
        )

    override suspend fun createEvent(payload: CreateEventPayload): AdminEventDetail =
        postPath("/v1/events", payload, serializer(), serializer())

    override suspend fun searchUsers(query: String): List<AdminUserSearchResult> =
        get("/users/search", mapOf("q" to query), ListSerializer(serializer<AdminUserSearchResult>()))

    override suspend fun createEventTeam(eventId: String, name: String, color: String?): AdminEventTeam =
        postPath("/v1/events/$eventId/teams", AddEventTeamPayload(name, color), serializer(), serializer())

    override suspend fun deleteEventTeam(eventId: String, teamId: String) =
        deletePath("/v1/events/$eventId/teams/$teamId")

    override suspend fun addEventParticipant(eventId: String, userId: String, eventTeamId: String?): AdminEventParticipant =
        postPath(
            "/v1/admin/events/$eventId/participants",
            AddEventParticipantPayload(userId, eventTeamId),
            serializer(),
            serializer(),
        )

    override suspend fun assignParticipantTeam(eventId: String, userId: String, eventTeamId: String) {
        putPath(
            "/v1/events/$eventId/participants/$userId/team",
            AssignEventTeamPayload(eventTeamId),
            serializer(),
            serializer<AdminEventParticipant>(),
        )
    }

    override suspend fun removeParticipantFromTeam(eventId: String, userId: String) =
        deletePath("/v1/events/$eventId/participants/$userId/team")

    override suspend fun applyEventTemplate(eventId: String, templateId: String): ApplyTemplateResult =
        postPath("/v1/admin/events/$eventId/apply-template/$templateId", Unit, serializer(), serializer())

    override suspend fun getClubs(page: Int, pageSize: Int): ClubListPage =
        getPath(
            "/v1/public/clubs",
            mapOf("page" to page.toString(), "size" to pageSize.toString()),
            serializer(),
        )

    override suspend fun createClub(request: CreateClubRequest): ClubRow =
        postPath("/v1/clubs", request, serializer(), serializer())

    override suspend fun getClub(clubId: String): ClubRow =
        getPath("/v1/clubs/$clubId", deserializer = serializer())

    override suspend fun updateClub(clubId: String, request: UpdateClubRequest): ClubRow =
        putPath("/v1/clubs/$clubId", request, serializer(), serializer())

    override suspend fun deleteClub(clubId: String) =
        deletePath("/v1/clubs/$clubId")

    override suspend fun uploadClubImage(
        bytes: ByteArray,
        fileName: String,
        contentType: String,
    ): String {
        val response = client.submitFormWithBinaryData(
            url = AppConfig.getApiUrl("/v1/clubs/upload-image"),
            formData = formData {
                append(
                    "file", bytes,
                    Headers.build {
                        append(HttpHeaders.ContentType, contentType)
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ) {
            // Let Ktor set the multipart boundary itself.
            headers.remove(HttpHeaders.ContentType)
        }
        return unwrapData("POST", "/v1/clubs/upload-image", response, serializer<ImageUploadResponse>())
            .imageUrl
    }

    override suspend fun uploadAppLaunchImage(
        bytes: ByteArray,
        fileName: String,
        contentType: String,
    ): String {
        val path = "/v1/admin/app-launch/upload-image"
        val response = client.submitFormWithBinaryData(
            url = AppConfig.getApiUrl(path),
            formData = formData {
                append(
                    "file", bytes,
                    Headers.build {
                        append(HttpHeaders.ContentType, contentType)
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ) {
            // Let Ktor set the multipart boundary itself.
            headers.remove(HttpHeaders.ContentType)
        }
        return unwrapData("POST", path, response, serializer<ImageUploadResponse>()).imageUrl
    }

    // ── Arena News moderation ──

    override suspend fun getNewsArticles(status: String?, page: Int, pageSize: Int): NewsArticlePage =
        get(
            "/news",
            buildMap {
                put("page", page.toString())
                put("size", pageSize.toString())
                status?.let { put("status", it) }
            },
            serializer<NewsArticlePage>(),
        )

    override suspend fun updateNewsArticle(id: String, payload: SaveNewsArticlePayload): NewsArticleRow =
        putPath("/v1/admin/news/$id", payload, serializer(), serializer())

    override suspend fun setNewsStatus(id: String, status: String, notify: Boolean): NewsArticleRow =
        putPath("/v1/admin/news/$id/status", NewsStatusPayload(status, notify), serializer(), serializer())

    override suspend fun setNewsFeaturedRank(id: String, rank: Int?): NewsArticleRow =
        putPath("/v1/admin/news/$id/featured", NewsFeaturedPayload(rank), serializer(), serializer())

    override suspend fun searchNewsMatches(sport: String, date: String, query: String): List<NewsMatchLink> =
        get(
            "/news/match-search",
            buildMap {
                put("sport", sport)
                put("date", date)
                if (query.isNotBlank()) put("q", query)
            },
            ListSerializer(serializer<NewsMatchLink>()),
        )

    override suspend fun setNewsMatchLink(id: String, match: NewsMatchLink?): NewsArticleRow =
        putPath("/v1/admin/news/$id/match-link", SetNewsMatchLinkPayload(match), serializer(), serializer())

    override suspend fun getFeaturedNews(): List<NewsArticleRow> =
        get("/news/featured", emptyMap(), ListSerializer(serializer<NewsArticleRow>()))

    override suspend fun appendFeaturedNews(id: String): NewsArticleRow {
        val path = "/v1/admin/news/$id/featured"
        val response = client.post(AppConfig.getApiUrl(path))
        return unwrapData("POST", path, response, serializer<NewsArticleRow>())
    }

    override suspend fun reorderFeaturedNews(ids: List<String>): List<NewsArticleRow> =
        putPath(
            "/v1/admin/news/featured",
            ReorderFeaturedPayload(ids),
            serializer(),
            ListSerializer(serializer<NewsArticleRow>()),
        )

    override suspend fun deleteNewsArticle(id: String) =
        deletePath("/v1/admin/news/$id")

    override suspend fun getNewsImageCandidates(id: String): NewsImageCandidates =
        get("/news/$id/image-candidates", emptyMap(), serializer())

    override suspend fun uploadNewsImage(
        bytes: ByteArray,
        fileName: String,
        contentType: String,
    ): String {
        val path = "/v1/admin/news/upload-image"
        val response = client.submitFormWithBinaryData(
            url = AppConfig.getApiUrl(path),
            formData = formData {
                append(
                    "file", bytes,
                    Headers.build {
                        append(HttpHeaders.ContentType, contentType)
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ) {
            // Let Ktor set the multipart boundary itself.
            headers.remove(HttpHeaders.ContentType)
        }
        return unwrapData("POST", path, response, serializer<ImageUploadResponse>()).imageUrl
    }

    override suspend fun runNewsIngest(): NewsIngestReport {
        val path = "/v1/admin/news/ingest"
        val response = client.post(AppConfig.getApiUrl(path))
        return unwrapData("POST", path, response, serializer<NewsIngestReport>())
    }

    override suspend fun runNewsRescore(): NewsRescoreReport {
        val path = "/v1/admin/news/rescore"
        val response = client.post(AppConfig.getApiUrl(path))
        return unwrapData("POST", path, response, serializer<NewsRescoreReport>())
    }

    override suspend fun updateClubGallery(clubId: String, imageUrls: List<String>): List<ClubGalleryImage> =
        putPath(
            "/v1/clubs/$clubId/gallery",
            UpdateClubGalleryRequest(imageUrls.map { GalleryImageEntry(it) }),
            serializer(),
            ListSerializer(serializer<ClubGalleryImage>()),
        )

    override suspend fun updateClubRules(clubId: String, rules: List<String>): List<ClubRule> =
        putPath(
            "/v1/clubs/$clubId/rules",
            UpdateClubRulesRequest(rules.map { RuleTextEntry(it) }),
            serializer(),
            ListSerializer(serializer<ClubRule>()),
        )

    override suspend fun getClubSchedule(clubId: String): ClubSchedule =
        getPath("/v1/clubs/$clubId/schedule", deserializer = serializer())

    override suspend fun updateClubWorkingHours(clubId: String, entries: List<WorkingHoursEntry>) {
        putPath(
            "/v1/clubs/$clubId/schedule/working-hours",
            UpdateWorkingHoursRequest(entries),
            serializer(),
            ListSerializer(serializer<WorkingHoursRow>()),
        )
    }

    override suspend fun getClubAmenities(clubId: String): List<String> =
        getPath("/v1/clubs/$clubId/amenities", deserializer = ListSerializer(serializer<String>()))

    override suspend fun updateClubAmenities(clubId: String, amenities: List<String>): List<String> =
        putPath(
            "/v1/clubs/$clubId/amenities",
            UpdateClubAmenitiesRequest(amenities),
            serializer(),
            ListSerializer(serializer<String>()),
        )

    override suspend fun getClubServices(clubId: String): List<ClubService> =
        getPath("/v1/clubs/$clubId/services", deserializer = ListSerializer(serializer<ClubService>()))

    override suspend fun createClubService(clubId: String, request: CreateClubServiceRequest): ClubService =
        postPath("/v1/clubs/$clubId/services", request, serializer(), serializer())

    override suspend fun updateClubService(
        clubId: String,
        serviceId: String,
        request: UpdateClubServiceRequest,
    ): ClubService =
        putPath("/v1/clubs/$clubId/services/$serviceId", request, serializer(), serializer())

    override suspend fun deleteClubService(clubId: String, serviceId: String) =
        deletePath("/v1/clubs/$clubId/services/$serviceId")

    override suspend fun getSports(): List<SportOption> =
        getPath("/v1/sports", deserializer = ListSerializer(serializer<SportOption>()))

    override suspend fun autocompleteAddress(query: String, session: String?): List<AddressSuggestion> =
        getPath(
            "/v1/geo/address-autocomplete",
            buildMap {
                put("query", query)
                if (session != null) put("session", session)
            },
            ListSerializer(serializer<AddressSuggestion>()),
        )

    override suspend fun getPlaceAddress(placeId: String, session: String?): PlaceAddress =
        getPath(
            "/v1/geo/address-place/$placeId",
            buildMap { if (session != null) put("session", session) },
            serializer(),
        )

    override suspend fun getSubscriptions(
        page: Int,
        pageSize: Int,
        query: String,
        status: SubscriptionStatusFilter,
    ): PageResult<SubscriptionRow> =
        get(
            "/subscriptions",
            mapOf(
                "page" to page.toString(),
                "pageSize" to pageSize.toString(),
                "query" to query,
                "status" to status.code,
            ),
            serializer(),
        )

    override suspend fun getApiRequests(period: Period): List<ApiRequestStats> =
        get(
            "/api/requests",
            mapOf("period" to period.code),
            ListSerializer(serializer<ApiRequestStats>()),
        )

    override suspend fun getApiRequestsByPlatform(period: Period): List<ApiPlatformBreakdown> =
        get(
            "/api/requests/by-platform",
            mapOf("period" to period.code),
            ListSerializer(serializer<ApiPlatformBreakdown>()),
        )

    override suspend fun getApiErrors(period: Period): List<ApiErrorRow> =
        get(
            "/api/errors",
            mapOf("period" to period.code),
            ListSerializer(serializer<ApiErrorRow>()),
        )

    override suspend fun getAuditEntries(limit: Int, offset: Long): List<AuditEntry> =
        get(
            "/audit",
            mapOf("limit" to limit.toString(), "offset" to offset.toString()),
            ListSerializer(serializer<AuditEntry>()),
        )

    // Launch popup admin endpoints live under /v1/admin/app-launch.
    override suspend fun getAppAnnouncements(): List<AppAnnouncementRow> =
        get("/app-launch/announcements", emptyMap(), ListSerializer(serializer<AppAnnouncementRow>()))

    override suspend fun createAppAnnouncement(payload: SaveAppAnnouncementPayload): AppAnnouncementRow =
        postPath("/v1/admin/app-launch/announcements", payload, serializer(), serializer())

    override suspend fun updateAppAnnouncement(id: String, payload: SaveAppAnnouncementPayload): AppAnnouncementRow =
        putPath("/v1/admin/app-launch/announcements/$id", payload, serializer(), serializer())

    override suspend fun deleteAppAnnouncement(id: String) =
        deletePath("/v1/admin/app-launch/announcements/$id")

    override suspend fun getAppUpdatePolicy(): AppUpdatePolicyMap =
        get("/app-launch/update-policy", emptyMap(), serializer())

    override suspend fun setAppUpdatePolicy(policy: AppUpdatePolicyMap) {
        putPath("/v1/admin/app-launch/update-policy", policy, serializer(), serializer<AppUpdatePolicyMap>())
    }

    // The support inbox lives at /v1/support/messages (admin-gated), not under /v1/admin.
    override suspend fun getSupportMessages(limit: Int, offset: Long): SupportMessagesPage =
        getPath(
            "/v1/support/messages",
            mapOf("limit" to limit.toString(), "offset" to offset.toString()),
            serializer<SupportMessagesPage>(),
        )

    override suspend fun getAllChats(page: Int, pageSize: Int, query: String): ChatListPage =
        get(
            "/chats",
            buildMap {
                put("page", page.toString())
                put("size", pageSize.toString())
                if (query.isNotBlank()) put("search", query)
            },
            serializer(),
        )

    override suspend fun getActiveStories(page: Int, pageSize: Int): AdminStoriesPage =
        get(
            "/social/stories",
            mapOf("page" to page.toString(), "size" to pageSize.toString()),
            serializer(),
        )

    override suspend fun getChatMessages(chatId: String, page: Int, pageSize: Int): ChatMessagesPage =
        get(
            "/chats/$chatId/messages",
            mapOf("page" to page.toString(), "size" to pageSize.toString()),
            serializer(),
        )

    override suspend fun deleteStory(storyId: String) =
        delete("/social/stories/$storyId")

    override suspend fun deletePost(postId: String, authorType: String) =
        delete("/social/posts/$postId?authorType=$authorType")

    override suspend fun getSportEventSizes(): List<SportEventSize> =
        get(
            "/sports/event-sizes",
            deserializer = ListSerializer(serializer<SportEventSize>()),
        )

    override suspend fun getRetentionCohorts(weeks: Int): List<RetentionCohort> =
        get(
            "/insights/retention",
            mapOf("weeks" to weeks.toString()),
            ListSerializer(serializer<RetentionCohort>()),
        )

    override suspend fun getSignupFunnel(): List<FunnelStep> =
        get(
            "/insights/funnel",
            deserializer = ListSerializer(serializer<FunnelStep>()),
        )

    override suspend fun getEventsByCity(limit: Int): List<CityEventCount> =
        get(
            "/insights/cities",
            mapOf("limit" to limit.toString()),
            ListSerializer(serializer<CityEventCount>()),
        )

    override suspend fun getEventLifecycle(): EventLifecycleStats =
        get("/insights/event-lifecycle", deserializer = serializer())

    override suspend fun getOnboardingCompletion(): OnboardingStats =
        get("/insights/onboarding", deserializer = serializer())

    override suspend fun getOnlineNow(): OnlineNow =
        get("/behavior/online", deserializer = serializer())

    override suspend fun getBehaviorKpis(period: Period): BehaviorKpis =
        get("/behavior/kpis", mapOf("period" to period.code), serializer())

    override suspend fun getBehaviorActiveUsersSeries(period: Period): CategorizedTimeSeries =
        get("/behavior/active-users/series", mapOf("period" to period.code), serializer())

    override suspend fun getBehaviorEventsSeries(period: Period): CategorizedTimeSeries =
        get("/behavior/events/series", mapOf("period" to period.code), serializer())

    override suspend fun getBehaviorTopScreens(period: Period, limit: Int): List<TopScreenRow> =
        get(
            "/behavior/screens/top",
            mapOf("period" to period.code, "limit" to limit.toString()),
            ListSerializer(serializer<TopScreenRow>()),
        )

    override suspend fun getBehaviorTopActions(period: Period, limit: Int): List<TopActionRow> =
        get(
            "/behavior/actions/top",
            mapOf("period" to period.code, "limit" to limit.toString()),
            ListSerializer(serializer<TopActionRow>()),
        )

    override suspend fun getBehaviorCountries(period: Period, limit: Int): List<CountryUsageRow> =
        get(
            "/behavior/countries",
            mapOf("period" to period.code, "limit" to limit.toString()),
            ListSerializer(serializer<CountryUsageRow>()),
        )

    override suspend fun getBehaviorDevices(period: Period): List<DeviceShareRow> =
        get(
            "/behavior/devices",
            mapOf("period" to period.code),
            ListSerializer(serializer<DeviceShareRow>()),
        )

    override suspend fun getBehaviorFirstSteps(period: Period, steps: Int, limit: Int): List<JourneyPathRow> =
        get(
            "/behavior/journeys/first-steps",
            mapOf(
                "period" to period.code,
                "steps" to steps.toString(),
                "limit" to limit.toString(),
            ),
            ListSerializer(serializer<JourneyPathRow>()),
        )

    override suspend fun getNewUserFunnel(period: Period): NewUserFunnel =
        get("/behavior/new-users/funnel", mapOf("period" to period.code), serializer())

    override suspend fun getNewUserActions(period: Period, limit: Int): List<TopActionRow> =
        get(
            "/behavior/new-users/actions",
            mapOf("period" to period.code, "limit" to limit.toString()),
            ListSerializer(serializer<TopActionRow>()),
        )

    override suspend fun getUserTimeline(userId: String, limit: Int): List<TimelineEvent> =
        get(
            "/behavior/users/$userId/timeline",
            mapOf("limit" to limit.toString()),
            ListSerializer(serializer<TimelineEvent>()),
        )

    override suspend fun getGeoDebug(): GeoDebug =
        get("/behavior/debug/geo", emptyMap(), serializer())

    override suspend fun getSessions(period: Period, limit: Int, offset: Int): SessionList =
        get(
            "/behavior/sessions",
            mapOf(
                "period" to period.code,
                "limit"  to limit.toString(),
                "offset" to offset.toString(),
            ),
            serializer(),
        )

    override suspend fun getSessionEvents(sessionId: String): List<TimelineEvent> =
        get(
            "/behavior/sessions/$sessionId/events",
            deserializer = ListSerializer(serializer<TimelineEvent>()),
        )

    // --- Content Studio (seeding fake content) ---

    override suspend fun seedUsers(request: SeedUsersRequest): List<SeedUserRow> =
        postPath("/v1/admin/seed/users", request, serializer(), ListSerializer(serializer<SeedUserRow>()))

    override suspend fun seedCustomUser(request: SeedCustomUserRequest): SeedUserRow =
        postPath("/v1/admin/seed/users/custom", request, serializer(), serializer())

    override suspend fun updateSeedUser(userId: String, request: SeedCustomUserRequest): SeedUserRow =
        putPath("/v1/admin/seed/users/$userId", request, serializer(), serializer())

    override suspend fun seedCustomPost(request: SeedCustomPostRequest): String =
        postPath("/v1/admin/seed/posts/custom", request, serializer(), serializer<SeedPostResult>()).id

    override suspend fun seedCustomStory(request: SeedCustomStoryRequest): String =
        postPath("/v1/admin/seed/stories/custom", request, serializer(), serializer<SeedPostResult>()).id

    override suspend fun uploadStoryImage(
        bytes: ByteArray,
        fileName: String,
        contentType: String,
    ): String {
        val response = client.submitFormWithBinaryData(
            url = AppConfig.getApiUrl("/v1/users/me/stories/upload-image"),
            formData = formData {
                append(
                    "file", bytes,
                    Headers.build {
                        append(HttpHeaders.ContentType, contentType)
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ) {
            headers.remove(HttpHeaders.ContentType)
        }
        return unwrapData("POST", "/v1/users/me/stories/upload-image", response, serializer<ImageUploadResponse>())
            .imageUrl
    }

    override suspend fun getSeedEventParticipants(eventId: String): List<SeedParticipant> =
        get("/seed/events/$eventId/participants", deserializer = ListSerializer(serializer<SeedParticipant>()))

    override suspend fun uploadEventImage(
        bytes: ByteArray,
        fileName: String,
        contentType: String,
    ): String {
        val response = client.submitFormWithBinaryData(
            url = AppConfig.getApiUrl("/v1/events/upload-image"),
            formData = formData {
                append(
                    "file", bytes,
                    Headers.build {
                        append(HttpHeaders.ContentType, contentType)
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ) {
            headers.remove(HttpHeaders.ContentType)
        }
        return unwrapData("POST", "/v1/events/upload-image", response, serializer<ImageUploadResponse>())
            .imageUrl
    }

    override suspend fun getSeedUsers(page: Int, pageSize: Int): PageResult<SeedUserRow> =
        get(
            "/seed/users",
            mapOf("page" to page.toString(), "pageSize" to pageSize.toString()),
            serializer(),
        )

    override suspend fun getSeedCardTemplates(): List<SeedCardTemplate> =
        get("/seed/card-templates", deserializer = ListSerializer(serializer<SeedCardTemplate>()))

    override suspend fun generateSeedAvatar(
        userId: String,
        cardTemplateId: String,
        bytes: ByteArray,
        fileName: String,
        contentType: String,
        prompt: String?,
    ): SeedAvatarResult {
        val path = "/v1/admin/seed/users/$userId/avatar"
        val response = client.submitFormWithBinaryData(
            url = AppConfig.getApiUrl(path),
            formData = formData {
                append("cardTemplateId", cardTemplateId)
                if (!prompt.isNullOrBlank()) append("prompt", prompt)
                append(
                    "file", bytes,
                    Headers.build {
                        append(HttpHeaders.ContentType, contentType)
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ) {
            // Let Ktor set the multipart boundary itself.
            headers.remove(HttpHeaders.ContentType)
        }
        return unwrapData("POST", path, response, serializer())
    }

    override suspend fun seedPosts(request: SeedPostsRequest): Int =
        postPath("/v1/admin/seed/posts", request, serializer(), serializer<SeedCount>()).created

    override suspend fun uploadPostImage(
        bytes: ByteArray,
        fileName: String,
        contentType: String,
    ): String {
        val response = client.submitFormWithBinaryData(
            url = AppConfig.getApiUrl("/v1/users/me/posts/upload-image"),
            formData = formData {
                append(
                    "file", bytes,
                    Headers.build {
                        append(HttpHeaders.ContentType, contentType)
                        append(HttpHeaders.ContentDisposition, "filename=\"$fileName\"")
                    },
                )
            },
        ) {
            headers.remove(HttpHeaders.ContentType)
        }
        return unwrapData("POST", "/v1/users/me/posts/upload-image", response, serializer<ImageUploadResponse>())
            .imageUrl
    }

    override suspend fun seedEvent(request: SeedEventRequest): SeedEventResult =
        postPath("/v1/admin/seed/events", request, serializer(), serializer())

    override suspend fun getSeedEvents(page: Int, pageSize: Int): PageResult<EventRow> =
        get(
            "/seed/events",
            mapOf("page" to page.toString(), "pageSize" to pageSize.toString()),
            serializer(),
        )

    override suspend fun addSeedParticipants(eventId: String, request: SeedParticipantsRequest): Int =
        postPath(
            "/v1/admin/seed/events/$eventId/participants",
            request,
            serializer(),
            serializer<SeedCount>(),
        ).created

    override suspend fun removeSeedParticipant(eventId: String, userId: String) =
        delete("/seed/events/$eventId/participants/$userId")

    override suspend fun updateSeedEventStatus(eventId: String, status: String): SeedEventStatusResult {
        val path = "/v1/admin/seed/events/$eventId"
        val response = client.patch(AppConfig.getApiUrl(path)) {
            setBody(
                TextContent(
                    json.encodeToString(serializer<SeedEventStatusRequest>(), SeedEventStatusRequest(status)),
                    ContentType.Application.Json,
                ),
            )
        }
        return unwrapData("PATCH", path, response, serializer())
    }

    override suspend fun deleteSeedEvent(eventId: String) =
        delete("/seed/events/$eventId")

    override suspend fun purgeSeedData(): SeedPurgeResult {
        val path = "/v1/admin/seed/purge"
        val response = client.delete(AppConfig.getApiUrl(path))
        return unwrapData("DELETE", path, response, serializer())
    }

    override suspend fun getAcquisitionSummary(period: Period): AcqSummaryDto =
        get("/acquisition/summary", mapOf("period" to period.code), serializer())

    override suspend fun getAcquisitionSources(period: Period): List<AcqSourceDto> =
        get("/acquisition/sources", mapOf("period" to period.code), ListSerializer(serializer<AcqSourceDto>()))

    override suspend fun getAcquisitionGeo(period: Period): AcqGeoDto =
        get("/acquisition/geo", mapOf("period" to period.code), serializer())

    override suspend fun getAcquisitionFunnel(period: Period): AcqFunnelDto =
        get("/acquisition/funnel", mapOf("period" to period.code), serializer())

    override suspend fun getAcquisitionInterests(period: Period, source: String?): List<AcqInterestDto> =
        get(
            "/acquisition/interests",
            buildMap {
                put("period", period.code)
                if (source != null) put("source", source)
            },
            ListSerializer(serializer<AcqInterestDto>()),
        )

    override suspend fun getPaywallFunnel(period: Period, platform: String?): PaywallFunnelDto =
        get(
            "/paywall/funnel",
            buildMap {
                put("period", period.code)
                if (platform != null) put("platform", platform)
            },
            serializer(),
        )
}
